import {
  useEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
  type WheelEvent as ReactWheelEvent,
} from 'react';
import { createPortal } from 'react-dom';
import { useT } from '@/lib/i18n';

const MAX_SCALE = 5;
const MIN_SCALE = 1;

interface Point {
  x: number;
  y: number;
}

export function PhotoLightbox({
  src,
  alt,
  details,
  onPrevious,
  onNext,
  canGoPrevious = false,
  canGoNext = false,
  onClose,
}: {
  src: string;
  alt: string;
  details?: ReactNode;
  onPrevious?: () => void;
  onNext?: () => void;
  canGoPrevious?: boolean;
  canGoNext?: boolean;
  onClose: () => void;
}) {
  const t = useT();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState<Point>({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const scaleRef = useRef(1);
  const positionRef = useRef<Point>({ x: 0, y: 0 });
  const pointersRef = useRef(new Map<number, Point>());
  const panRef = useRef({ start: { x: 0, y: 0 }, origin: { x: 0, y: 0 } });
  const pinchRef = useRef({
    distance: 0,
    scale: 1,
    midpoint: { x: 0, y: 0 },
    origin: { x: 0, y: 0 },
  });
  const lastTapRef = useRef({ at: 0, x: 0, y: 0 });
  const onCloseRef = useRef(onClose);
  const onPreviousRef = useRef(onPrevious);
  const onNextRef = useRef(onNext);
  const canGoPreviousRef = useRef(canGoPrevious);
  const canGoNextRef = useRef(canGoNext);
  onCloseRef.current = onClose;
  onPreviousRef.current = onPrevious;
  onNextRef.current = onNext;
  canGoPreviousRef.current = canGoPrevious;
  canGoNextRef.current = canGoNext;

  function clampPosition(next: Point, nextScale: number): Point {
    const image = imageRef.current;
    const container = containerRef.current;
    if (!image || !container) return next;
    const maxX = Math.max(
      0,
      (image.offsetWidth * nextScale - container.clientWidth) / 2,
    );
    const maxY = Math.max(
      0,
      (image.offsetHeight * nextScale - container.clientHeight) / 2,
    );
    return {
      x: Math.min(maxX, Math.max(-maxX, next.x)),
      y: Math.min(maxY, Math.max(-maxY, next.y)),
    };
  }

  function applyTransform(nextScale: number, nextPosition: Point) {
    scaleRef.current = nextScale;
    positionRef.current = nextPosition;
    setScale(nextScale);
    setPosition(nextPosition);
  }

  function reset() {
    applyTransform(1, { x: 0, y: 0 });
  }

  function zoomBy(factor: number) {
    const nextScale = Math.min(
      MAX_SCALE,
      Math.max(MIN_SCALE, scaleRef.current * factor),
    );
    if (nextScale === 1) {
      reset();
      return;
    }
    const ratio = nextScale / scaleRef.current;
    const nextPosition = clampPosition(
      {
        x: positionRef.current.x * ratio,
        y: positionRef.current.y * ratio,
      },
      nextScale,
    );
    applyTransform(nextScale, nextPosition);
  }

  useEffect(() => {
    reset();
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onCloseRef.current();
        return;
      }
      if (event.key === 'ArrowLeft' && canGoPreviousRef.current) {
        event.preventDefault();
        onPreviousRef.current?.();
      }
      if (event.key === 'ArrowRight' && canGoNextRef.current) {
        event.preventDefault();
        onNextRef.current?.();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [src]);

  function pointerPoint(event: ReactPointerEvent<HTMLDivElement>): Point {
    return { x: event.clientX, y: event.clientY };
  }

  function handlePointerDown(event: ReactPointerEvent<HTMLDivElement>) {
    event.currentTarget.setPointerCapture(event.pointerId);
    pointersRef.current.set(event.pointerId, pointerPoint(event));
    setDragging(true);

    if (pointersRef.current.size === 1) {
      const point = pointerPoint(event);
      const now = Date.now();
      const last = lastTapRef.current;
      const isDoubleTap =
        now - last.at < 320 &&
        Math.hypot(point.x - last.x, point.y - last.y) < 36;
      lastTapRef.current = { at: now, x: point.x, y: point.y };
      if (isDoubleTap) {
        if (scaleRef.current > 1) reset();
        else {
          const nextScale = 2.5;
          const container = containerRef.current?.getBoundingClientRect();
          if (container) {
            const center = {
              x: container.left + container.width / 2,
              y: container.top + container.height / 2,
            };
            const nextPosition = clampPosition(
              {
                x: (center.x - point.x) * (nextScale - 1),
                y: (center.y - point.y) * (nextScale - 1),
              },
              nextScale,
            );
            applyTransform(nextScale, nextPosition);
          } else {
            applyTransform(nextScale, { x: 0, y: 0 });
          }
        }
        return;
      }
      panRef.current = {
        start: point,
        origin: positionRef.current,
      };
    } else if (pointersRef.current.size === 2) {
      const points = [...pointersRef.current.values()];
      const a = points[0]!;
      const b = points[1]!;
      pinchRef.current = {
        distance: Math.hypot(b.x - a.x, b.y - a.y),
        scale: scaleRef.current,
        midpoint: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 },
        origin: positionRef.current,
      };
    }
  }

  function handlePointerMove(event: ReactPointerEvent<HTMLDivElement>) {
    if (!pointersRef.current.has(event.pointerId)) return;
    pointersRef.current.set(event.pointerId, pointerPoint(event));

    if (pointersRef.current.size === 1 && scaleRef.current > 1) {
      const point = pointerPoint(event);
      const next = clampPosition(
        {
          x: panRef.current.origin.x + point.x - panRef.current.start.x,
          y: panRef.current.origin.y + point.y - panRef.current.start.y,
        },
        scaleRef.current,
      );
      applyTransform(scaleRef.current, next);
      return;
    }

    if (pointersRef.current.size >= 2) {
      const points = [...pointersRef.current.values()];
      const a = points[0]!;
      const b = points[1]!;
      const distance = Math.hypot(b.x - a.x, b.y - a.y);
      const midpoint = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
      const start = pinchRef.current;
      const nextScale = Math.min(
        MAX_SCALE,
        Math.max(
          MIN_SCALE,
          start.scale * (distance / Math.max(start.distance, 1)),
        ),
      );
      const container = containerRef.current?.getBoundingClientRect();
      if (!container) return;
      const ratio = nextScale / start.scale;
      const center = {
        x: container.left + container.width / 2,
        y: container.top + container.height / 2,
      };
      const nextPosition = clampPosition(
        {
          x:
            midpoint.x -
            center.x -
            (start.midpoint.x - center.x - start.origin.x) * ratio,
          y:
            midpoint.y -
            center.y -
            (start.midpoint.y - center.y - start.origin.y) * ratio,
        },
        nextScale,
      );
      applyTransform(nextScale, nextPosition);
    }
  }

  function endPointer(event: ReactPointerEvent<HTMLDivElement>) {
    pointersRef.current.delete(event.pointerId);
    if (pointersRef.current.size === 0) {
      setDragging(false);
    } else if (pointersRef.current.size === 1) {
      const point = [...pointersRef.current.values()][0]!;
      panRef.current = { start: point, origin: positionRef.current };
    }
  }

  function handleWheel(event: ReactWheelEvent<HTMLDivElement>) {
    event.preventDefault();
    zoomBy(event.deltaY < 0 ? 1.15 : 1 / 1.15);
  }

  return createPortal(
    <div
      className="fixed inset-0 z-[2000] flex flex-col bg-black/95 text-white"
      role="dialog"
      aria-modal="true"
      aria-label={alt}
    >
      <div className="flex shrink-0 items-center justify-between px-4 py-3">
        <span className="text-xs text-white/60">
          {Math.round(scale * 100)}%
        </span>
        <button
          type="button"
          onClick={onClose}
          className="flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-2xl leading-none hover:bg-white/20"
          aria-label={t('Закрыть')}
          title={t('Закрыть')}
        >
          ×
        </button>
      </div>
      <div
        ref={containerRef}
        className="relative flex min-h-0 flex-1 touch-none select-none items-center justify-center overflow-hidden"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={endPointer}
        onPointerCancel={endPointer}
        onWheel={handleWheel}
      >
        <img
          ref={imageRef}
          src={src}
          alt={alt}
          draggable={false}
          onLoad={reset}
          className="max-h-full max-w-full select-none object-contain"
          style={{
            transform: `translate3d(${position.x}px, ${position.y}px, 0) scale(${scale})`,
            transformOrigin: 'center center',
            transition: dragging ? 'none' : 'transform 120ms ease-out',
          }}
        />
      </div>
      {details && (
        <div className="max-h-[32vh] shrink-0 overflow-y-auto border-t border-white/10 px-4 py-3 text-sm">
          {details}
        </div>
      )}
      <div className="flex shrink-0 items-center justify-center gap-2 px-4 py-4">
        {onPrevious && (
          <button
            type="button"
            onClick={onPrevious}
            disabled={!canGoPrevious}
            className="flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-xl hover:bg-white/20 disabled:cursor-default disabled:opacity-30"
            aria-label={t('Предыдущее фото')}
            title={t('Предыдущее фото')}
          >
            ←
          </button>
        )}
        <button
          type="button"
          onClick={() => zoomBy(1 / 1.5)}
          className="flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-xl hover:bg-white/20"
          aria-label={t('Уменьшить')}
          title={t('Уменьшить')}
        >
          −
        </button>
        <button
          type="button"
          onClick={reset}
          className="rounded-full bg-white/10 px-4 py-2 text-xs font-medium hover:bg-white/20"
        >
          1×
        </button>
        <button
          type="button"
          onClick={() => zoomBy(1.5)}
          className="flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-xl hover:bg-white/20"
          aria-label={t('Увеличить')}
          title={t('Увеличить')}
        >
          +
        </button>
        {onNext && (
          <button
            type="button"
            onClick={onNext}
            disabled={!canGoNext}
            className="flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-xl hover:bg-white/20 disabled:cursor-default disabled:opacity-30"
            aria-label={t('Следующее фото')}
            title={t('Следующее фото')}
          >
            →
          </button>
        )}
      </div>
    </div>,
    document.body,
  );
}
